import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

// ==========================================
// TYPE DEFINITIONS - Matching Backend Schema
// ==========================================

// Common Types
export type TimeRange = 'week' | 'month' | 'quarter' | 'year';
export type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type Trend = 'up' | 'down' | 'stable';
export type Priority = 'high' | 'medium' | 'low';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type AnalyticsType = 'course' | 'certification';

// 1. Analytics Overview Types
export interface OverviewStatsParams {
  time_range?: TimeRange;
  compare_previous?: boolean;
  department_filter?: string;
  granularity?: Granularity;
  start_date?: string;
  end_date?: string;
  analytics_type?: AnalyticsType;
}

export interface StatisticValue {
  value: number;
  change: number;
  trend: Trend;
  previous_value?: number;
  percentage_change?: number;
}

export interface EmployeeStatistic extends StatisticValue {}

export interface ActiveLearningRate extends StatisticValue {
  active_learners_count: number;
  total_employees: number;
}

export interface CompletionRate extends StatisticValue {
  total_courses: number;
  completed_courses: number;
}

export interface TrainingROI extends StatisticValue {
  investment_amount?: number;
  return_amount?: number;
}

export interface ComplianceRate extends StatisticValue {
  mandatory_courses_count: number;
  completed_mandatory_count: number;
}

export interface TimeToComplete extends StatisticValue {
  unit: 'days' | 'hours' | 'minutes';
}

export interface AnalyticsOverviewResponse {
  total_employees: EmployeeStatistic;
  active_learning_rate: ActiveLearningRate;
  avg_completion_rate: CompletionRate;
  training_roi: TrainingROI;
  compliance_rate: ComplianceRate;
  avg_time_to_complete: TimeToComplete;
  period: {
    start_date: string;
    end_date: string;
    granularity: string;
  };
}

// 2. Department Performance Types
export interface DepartmentPerformanceParams {
  time_range?: TimeRange;
  department_ids?: string[];
  sort_by?: 'name' | 'employees' | 'active_rate' | 'completion_rate' | 'avg_score';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  start_date?: string;
  end_date?: string;
  analytics_type?: AnalyticsType;
}

export interface TrainingStatusParams {
  time_range?: TimeRange;
  start_date?: string;
  end_date?: string;
  analytics_type?: AnalyticsType;
}

export interface DepartmentMetric {
  department_id: string;
  department_name: string;
  employees_count: number;
  active_rate: number;
  completion_rate: number;
  avg_score: number;
  time_spent_hours: number;
  time_spent_seconds: number;
  trend_percentage: number;
  courses_assigned: number;
  courses_completed: number;
  certificates_earned: number;
  last_updated: string;
}

export interface DepartmentPerformanceResponse {
  departments: DepartmentMetric[];
  total_departments: number;
  period: {
    start_date: string;
    end_date: string;
  };
}
export interface TrainingStatusResponse {
    completed_count: number;
    in_progress_count: number;
    not_started_count: number;
    overdue_count: number;
}

// 3. Learning Trends Types
export interface LearningTrendsParams {
  time_range?: TimeRange;
  granularity?: 'daily' | 'weekly' | 'monthly';
  metric_type?: 'all' | 'enrollments' | 'completions' | 'hours';
  department_filter?: string;
  course_filter?: string;
  start_date?: string;
  end_date?: string;
  analytics_type?: AnalyticsType;
}

export interface LearningTrendPoint {
  period: string;
  period_label: string;
  // Course analytics fields
  course_enrollments?: number;
  courses_completed?: number;
  // Certification analytics fields
  certification_enrollments?: number;
  certifications_completed?: number;
  training_hours: number;
  training_seconds: number;
  completion_rate: number;
  active_users: number;
  new_users_onboarded: number;
}

export interface LearningTrendsResponse {
  trends: LearningTrendPoint[];
  summary: {
    total_enrollments: number;
    total_completions: number;
    total_hours: number;
    period_change_percentage: number;
    best_performing_period: string;
  };
  period: {
    start_date: string;
    end_date: string;
    granularity: string;
  };
}

// 4. Skill Gap Types
export interface SkillGapParams {
  department_filter?: string;
  priority_level?: Priority | 'all';
  skill_category?: string;
  limit?: number;
  sort_by?: 'gap_percentage' | 'affected_employees' | 'priority' | 'skill_name';
  sort_order?: 'asc' | 'desc';
}

export interface SkillGap {
  skill_id: string;
  skill_name: string;
  skill_category: string;
  required_proficiency: number;
  current_proficiency: number;
  gap_percentage: number;
  employees_lacking: number;
  priority: Priority;
  departments_affected: string[];
  recommended_training_hours: number;
  expected_improvement: number;
  priority_score: number;
}

export interface SkillRecommendation {
  skill_id: string;
  skill_name: string;
  recommendation_type: 'immediate' | 'short_term' | 'long_term';
  suggested_courses: Array<{
    course_id: string;
    course_name: string;
    duration_hours: number;
  }>;
  target_audience: string[];
  estimated_time_to_close_gap: number;
  priority: Priority;
  business_impact: string;
}

export interface SkillGapAnalysisResponse {
  skill_gaps: SkillGap[];
  total_gaps_identified: number;
  high_priority_count: number;
  departments_affected: number;
  recommendations: SkillRecommendation[];
}

// 5. Top Performers Types
export interface TopPerformersParams {
  time_range?: TimeRange;
  metric_type?: 'completion_rate' | 'score' | 'speed' | 'engagement' | 'overall';
  department_filter?: string;
  limit?: number;
  exclude_admins?: boolean;
  start_date?: string;
  end_date?: string;
  analytics_type?: AnalyticsType;
}

export interface TopPerformer {
  rank: number;
  user_id: string;
  user_name: string;
  email: string;
  department: string;
  overall_score: number;
  // Course analytics field
  courses_completed?: number;
  // Certification analytics field
  certifications_completed?: number;
  completion_rate: number;
  average_score: number;
  learning_hours: number;
  badges: string[];
  recent_achievements: string[];
  streak_days: number;
  improvement_percentage: number;
}

export interface TopPerformersResponse {
  performers: TopPerformer[];
  leaderboard_stats: {
    total_participants: number;
    departments_represented: number;
    avg_completion_rate: number;
    period_start: string;
    period_end: string;
  };
}

// 6. Training Deadlines Types
export interface TrainingDeadlinesParams {
  days_ahead?: number;
  from_date?: string;
  to_date?: string;
  priority_level?: Priority | 'all';
  department_filter?: string;
  course_filter?: string;
  include_overdue?: boolean;
  status_filter?: 'upcoming' | 'overdue' | 'at_risk' | 'all';
  limit?: number;
  page?: number;
  skip?: number;
  sort_by?: 'deadline_date' | 'affected_count' | 'priority' | 'course_name';
  analytics_type?: AnalyticsType;
}

export interface DepartmentDeadlineBreakdown {
  department_id: string;
  department_name: string;
  assigned_count: number;
  completed_count: number;
  completion_rate: number;
  at_risk_count: number;
}

export interface DeadlineUser {
  user_id: string;
  name: string;
  email: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  expired: boolean;
  notify: boolean;
}

export interface TrainingDeadline {
  assignment_id: string;
  course_id?: string;
  course_name?: string;
  course_description?: string;
  // Certification analytics fields
  certification_id?: string;
  certification_name?: string;
  certification_description?: string;
  deadline_date: string;
  days_remaining: number;
  priority: Priority;
  is_mandatory: boolean;
  total_assigned: number;
  completed_count: number;
  in_progress_count: number;
  not_started_count: number;
  overdue_count: number;
  completion_rate: number;
  departments: DepartmentDeadlineBreakdown[];
  risk_level: RiskLevel;
  estimated_completion_date?: string;
  assigned_date: string;
  users?: DeadlineUser[];
  assigned_by?: {
    user_id: string;
    name: string;
  };
}

export interface TrainingDeadlinesResponse {
  data: TrainingDeadline[];
  deadlines?: TrainingDeadline[]; // Legacy support
  total: number;
  page: number;
  limit: number;
  total_assigned: number;
  summary: {
    total_upcoming: number;
    total_overdue: number;
    total_at_risk: number;
    high_priority_count: number;
    affected_employees_total: number;
  };
  period?: {
    from_date: string;
    to_date: string;
  };
}

// 7. Send Deadline Reminder Types
export interface SendDeadlineReminderParams {
  user_id: string;
  type: AnalyticsType;
  course_id?: string;
  certification_id?: string;
}

export interface SendDeadlineReminderResponse {
  success: boolean;
  message: string;
}

// ==========================================
// SERVICE CLASS
// ==========================================

class AnalyticsService {
  /**
   * Get analytics overview statistics
   */
  async getOverviewStats(params?: OverviewStatsParams): Promise<AnalyticsOverviewResponse> {
    try {
      const body: any = {};

      // Handle custom date range or time_range
      if (params?.start_date && params?.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params?.time_range) {
        body.time_range = params.time_range;
      }

      if (params?.compare_previous !== undefined) body.compare_previous = params.compare_previous;
      if (params?.department_filter) body.department_filter = params.department_filter;
      if (params?.granularity) body.granularity = params.granularity;
      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<AnalyticsOverviewResponse>(API_ENDPOINTS.ANALYTICS.OVERVIEW, body);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch analytics overview:', error);
      throw error;
    }
  }

  /**
   * Get department performance metrics
   */
  async getDepartmentPerformance(params?: DepartmentPerformanceParams): Promise<DepartmentPerformanceResponse> {
    try {
      const body: any = {};

      // Handle custom date range or time_range
      if (params?.start_date && params?.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params?.time_range) {
        body.time_range = params.time_range;
      }

      if (params?.department_ids?.length) body.department_ids = params.department_ids;
      if (params?.sort_by) body.sort_by = params.sort_by;
      if (params?.sort_order) body.sort_order = params.sort_order;
      if (params?.limit) body.limit = params.limit;
      if (params?.offset) body.offset = params.offset;
      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<DepartmentPerformanceResponse>(
        API_ENDPOINTS.ANALYTICS.DEPARTMENT_PERFORMANCE,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch department performance:', error);
      throw error;
    }
  }

  /**
   * Get learning activity trends
   */
  async getLearningTrends(params?: LearningTrendsParams): Promise<LearningTrendsResponse> {
    try {
      const body: any = {};

      // Handle custom date range or time_range
      if (params?.start_date && params?.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params?.time_range) {
        body.time_range = params.time_range;
      }

      if (params?.granularity) body.granularity = params.granularity;
      if (params?.metric_type) body.metric_type = params.metric_type;
      if (params?.department_filter) body.department_filter = params.department_filter;
      if (params?.course_filter) body.course_filter = params.course_filter;
      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<LearningTrendsResponse>(
        API_ENDPOINTS.ANALYTICS.LEARNING_TRENDS,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch learning trends:', error);
      throw error;
    }
  }

  /**
   * Get skill gap analysis
   */
  async getSkillGaps(params?: SkillGapParams): Promise<SkillGapAnalysisResponse> {
    try {
      const body: any = {};

      if (params?.department_filter) body.department_filter = params.department_filter;
      if (params?.priority_level) body.priority_level = params.priority_level;
      if (params?.skill_category) body.skill_category = params.skill_category;
      if (params?.limit) body.limit = params.limit;
      if (params?.sort_by) body.sort_by = params.sort_by;
      if (params?.sort_order) body.sort_order = params.sort_order;

      const response = await apiClient.post<SkillGapAnalysisResponse>(
        API_ENDPOINTS.ANALYTICS.SKILL_GAPS,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch skill gaps:', error);
      throw error;
    }
  }

  /**
   * Get top performers leaderboard
   */
  async getTopPerformers(params?: TopPerformersParams): Promise<TopPerformersResponse> {
    try {
      const body: any = {};

      // Handle custom date range or time_range
      if (params?.start_date && params?.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params?.time_range) {
        body.time_range = params.time_range;
      }

      if (params?.metric_type) body.metric_type = params.metric_type;
      if (params?.department_filter) body.department_filter = params.department_filter;
      if (params?.limit) body.limit = params.limit;
      if (params?.exclude_admins !== undefined) body.exclude_admins = params.exclude_admins;
      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<TopPerformersResponse>(
        API_ENDPOINTS.ANALYTICS.TOP_PERFORMERS,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch top performers:', error);
      throw error;
    }
  }

  /**
   * Get training deadlines and compliance
   */
  async getTrainingDeadlines(params?: TrainingDeadlinesParams): Promise<TrainingDeadlinesResponse> {
    try {
      const body: any = {};

      if (params?.days_ahead) body.days_ahead = params.days_ahead;
      if (params?.from_date) body.from_date = params.from_date;
      if (params?.to_date) body.to_date = params.to_date;
      if (params?.priority_level) body.priority_level = params.priority_level;
      if (params?.department_filter) body.department_filter = params.department_filter;
      if (params?.course_filter) body.course_filter = params.course_filter;
      if (params?.include_overdue !== undefined) body.include_overdue = params.include_overdue;
      if (params?.status_filter) body.status_filter = params.status_filter;
      if (params?.limit) body.limit = params.limit;
      if (params?.page) body.page = params.page;
      if (params?.skip !== undefined) body.skip = params.skip;
      if (params?.sort_by) body.sort_by = params.sort_by;
      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<TrainingDeadlinesResponse>(
        API_ENDPOINTS.ANALYTICS.TRAINING_DEADLINES,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch training deadlines:', error);
      throw error;
    }
  }


  //Get training status overview

   async getTrainingStatus(params?: TrainingStatusParams): Promise<TrainingStatusResponse> {
    try {
      const body: any = {};

      // Handle custom date range or time_range
      if (params?.start_date && params?.end_date) {
        body.start_date = params.start_date;
        body.end_date = params.end_date;
      } else if (params?.time_range) {
        body.time_range = params.time_range;
      }

      if (params?.analytics_type) body.type = params.analytics_type;

      const response = await apiClient.post<TrainingStatusResponse>(
        API_ENDPOINTS.ANALYTICS.TRAINING_STATUS,
        body
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch training status:', error);
      throw error;
    }
  }

  /**
   * Send deadline reminder email to a user
   */
  async sendDeadlineReminder(params: SendDeadlineReminderParams): Promise<SendDeadlineReminderResponse> {
    try {
      const response = await apiClient.post<SendDeadlineReminderResponse>(
        API_ENDPOINTS.ANALYTICS.SEND_DEADLINE_REMINDER,
        params
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send deadline reminder:', error);
      throw error;
    }
  }

  // ==========================================
  // HELPER METHODS FOR UI TRANSFORMATIONS
  // ==========================================

  /**
   * Transform backend metric to UI format
   * Maps backend field names to UI expected names
   */
  transformMetricCard(metric: StatisticValue, title: string, subtitle: string, icon: React.ReactNode, color: string) {
    return {
      title,
      value: metric.value,
      change: `${metric.trend === 'up' ? '+' : metric.trend === 'down' ? '-' : ''}${Math.abs(metric.change)}${
        metric.percentage_change ? '%' : ''
      }`,
      trend: metric.trend,
      icon,
      color,
      subtitle,
    };
  }

  /**
   * Transform department metric from backend to UI format
   */
  transformDepartmentMetric(dept: DepartmentMetric) {
    return {
      name: dept.department_name,
      employees: dept.employees_count,
      activeRate: dept.active_rate,
      completionRate: dept.completion_rate,
      avgScore: dept.avg_score,
      timeSpent: dept.time_spent_hours,
      trend: dept.trend_percentage,
    };
  }

  /**
   * Transform skill gap from backend to UI format
   */
  transformSkillGap(gap: SkillGap) {
    return {
      skill: gap.skill_name,
      required: gap.required_proficiency,
      current: gap.current_proficiency,
      gap: gap.gap_percentage,
      priority: gap.priority,
      affectedEmployees: gap.employees_lacking,
    };
  }

  /**
   * Transform top performer from backend to UI format
   */
  transformTopPerformer(performer: TopPerformer) {
    return {
      name: performer.user_name,
      department: performer.department,
      courses: performer.courses_completed,
      avgScore: performer.average_score,
      rank: performer.rank,
    };
  }

  /**
   * Transform training deadline from backend to UI format
   */
  transformTrainingDeadline(deadline: TrainingDeadline) {
    return {
      course: deadline.course_name,
      deadline: deadline.deadline_date,
      affected: deadline.total_assigned,
      priority: deadline.priority,
    };
  }

  /**
   * Transform learning trend point from backend to UI format
   */
  transformLearningTrend(trend: LearningTrendPoint) {
    return {
      month: trend.period_label,
      enrollments: trend.course_enrollments,
      completions: trend.courses_completed,
      hours: trend.training_seconds,
    };
  }
}

export const analyticsService = new AnalyticsService();